import { defineStore } from 'pinia';
import { ref } from 'vue';

import { getImageUrl } from '@apps/main/modules/api/hotels';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import type {
  ContentApiImage,
  SmartSearchImageUrl,
} from '@libs/trip-planner-interfaces';

import type { HotelDetailsFullscreenMasonryGalleryData } from '../models';
import { HotelDetailsGalleryType } from '../models';

import { useHotelDetailsPanelStore } from './hotelDetailsPanelStore';

export const useHotelDetailsGalleryStore = defineStore(
  'HotelDetailsGalleryStore',
  () => {
    const hotelDetailsPanelStore = useHotelDetailsPanelStore();
    const browserStore = useBrowserStore();

    // State
    const currentOpenedGallery = ref<HotelDetailsGalleryType | undefined>(
      undefined
    );
    const currentSlideIndex = ref<number>(0);

    // Actions
    function openHotelDetailsFullscreenMasonryGallery(
      imagesURL: ContentApiImage[]
    ): void {
      hotelDetailsPanelStore.setGalleryImages(imagesURL);
      currentOpenedGallery.value = HotelDetailsGalleryType.MasonryForHotel;
    }

    function openFullScreenSingleImageGallery(
      data: HotelDetailsFullscreenMasonryGalleryData,
      type:
        | HotelDetailsGalleryType.SingleImageForHotel
        | HotelDetailsGalleryType.SingleImageForRoom
    ): void {
      currentSlideIndex.value = data.index;
      hotelDetailsPanelStore.setGalleryImages(data.images);

      currentOpenedGallery.value = type;
    }

    function closeHotelDetailsGallery(): void {
      if (
        currentOpenedGallery.value ===
        HotelDetailsGalleryType.SingleImageForHotel
      ) {
        currentOpenedGallery.value = HotelDetailsGalleryType.MasonryForHotel;
      } else {
        resetGalleryState();
      }
    }

    function resetGalleryState(): void {
      hotelDetailsPanelStore.setGalleryImages(undefined);
      currentOpenedGallery.value = undefined;
    }

    function getImageUrlWithQuality(
      image: ContentApiImage | SmartSearchImageUrl,
      quality: 'small' | 'normal' | 'good' | 'large'
    ): string {
      const smallSize = 600;
      const normalSize = 800;
      const goodSize = 1200;
      const largeSize = 1600;

      if (typeof image === 'string') {
        let imageWithSize: string = image;
        if (quality === 'small') {
          imageWithSize += `&width=${smallSize}&height=${smallSize}`;
        } else if (quality === 'good') {
          imageWithSize += `&width=${goodSize}&height=${goodSize}`;
        } else if (quality === 'large') {
          imageWithSize += `&width=${largeSize}&height=${largeSize}`;
        } else {
          imageWithSize += `&width=${normalSize}&height=${normalSize}`;
        }

        return imageWithSize;
      } else {
        if (quality === 'small') {
          return getImageUrl(image, undefined, smallSize, smallSize);
        }

        if (quality === 'good') {
          return getImageUrl(image, undefined, goodSize, goodSize);
        }

        if (quality === 'large') {
          return getImageUrl(image, undefined, largeSize, largeSize);
        }

        return getImageUrl(image, undefined, normalSize, normalSize);
      }
    }

    function getImagesForFullScreenGallery(
      images: ContentApiImage[] | string[]
    ): string[] {
      const quality = browserStore.isSmallerThan1200px ? 'small' : 'good';
      return images.map((image) => getImageUrlWithQuality(image, quality));
    }

    function getImagesForSingleImageGallery(
      images: ContentApiImage[] | string[]
    ): string[] {
      const quality = browserStore.isSmallerThan1200px ? 'small' : 'large';
      return images.map((image) => getImageUrlWithQuality(image, quality));
    }

    function getImagesForSwipperGallery(
      images: ContentApiImage[] | string[]
    ): string[] {
      return images.map((image) => getImageUrlWithQuality(image, 'small'));
    }

    function getImagesForMasonryGallery(
      images: ContentApiImage[] | SmartSearchImageUrl[]
    ): string[] {
      const gallerySize = 5;
      const firstImage = images[0];
      if (firstImage !== undefined && images.length > 0) {
        return [
          getImageUrlWithQuality(firstImage, 'normal'),
          ...images
            .slice(1, gallerySize)
            .map((image) => getImageUrlWithQuality(image, 'small')),
        ];
      }
      return [];
    }

    return {
      // State
      currentOpenedGallery,
      currentSlideIndex,

      // Actions
      openHotelDetailsFullscreenMasonryGallery,
      openFullScreenSingleImageGallery,
      closeHotelDetailsGallery,
      resetGalleryState,
      getImagesForFullScreenGallery,
      getImagesForSingleImageGallery,
      getImagesForSwipperGallery,
      getImagesForMasonryGallery,
      getImageUrlWithQuality,
    };
  }
);
